import { gql, TypedDocumentNode } from '@apollo/client';

import {
  RelatedFieldAutocomplete,
  RelatedFieldInput,
  Task,
  TaskAssetRemoveInput,
  TaskAssetsAddInput,
  TaskCreateInput,
  TaskField,
  TaskFieldCreateInput,
  TaskFieldDeleteInput,
  TaskFieldUpdateInput,
  TaskMaterialItem,
  TaskMaterialsAddInput,
  TaskUpdateInput,
  TaskUserGroupRemoveInput,
  TaskUserGroupsAddInput,
  TaskUserRemoveInput,
  TaskUsersAddInput,
  TaskUsersStatusUpdateInput,
} from '@atom/types/task';

export const BASE_TASK_FIELD = gql`
  fragment BaseTaskField on TaskField {
    id
    dataType
    title
    required
    selectAllOnCompleteWork
    value
    enumeration
    defaultValue
    subFieldsTruncated
    subFields {
      matchValue
      nestedField
    }
    units
    hyperlink
    viewAs
  }
`;

export const BASE_TASK = gql`
  fragment BaseTask on Task {
    id
    name
    originalName
    duration
    dueDate
    completionDate
    completedBy
    completedByName
    description
    estimatedCost
    isCompleted
    updatedDate
    startTime
    workTime
    actualCost
    isEstimatedCostOverridden
    taskTemplateId
    createdBy

    assetIds
    formInstanceIds

    users {
      id
      firstName
      lastName
      email
      photoUrl
      status
      workTime
      actualCost
      employeeId

      budget {
        budgetId
        name
        rate
      }
    }

    materials {
      name
      assetId
      schemaId
      colorId
      markerId
      locationType
      isStockBased
      isStartEndReading
      isStartEndCalculated
      rate
      cost
      quantity
      unit
    }

    userGroups {
      userGroupId
      name
      colorId
      quantity
      workTime
      userCount

      budget {
        budgetId
        name
        rate
        restored
        reopenedWorkOrderIds
      }
    }

    fieldOrder
    fields {
      ...BaseTaskField
    }

    categoryIds
    categories {
      id
      name
      categoryPath
    }

    locations {
      id
      name
    }

    locationsEnabled
    requireAtLeastOneLocation
    multiAssetsEnabled
    allowedMultiAssetSchemaIds
    requireAtLeastOneMultiAsset
    requiredMultiAssetSchemaIds
  }
  ${BASE_TASK_FIELD}
`;

export const BASE_TASK_ITEM = gql`
  fragment BaseTaskItem on TaskConnectionItem {
    id
    name
    formInstanceIds
    locationIds

    dueDate
    completedDate
    startTime
    duration

    users {
      id
      firstName
      lastName
      email
      photoUrl
      status
    }

    userGroups {
      id
      name
      colorId
    }

    fields {
      ...BaseTaskField
    }

    locationsEnabled
    requireAtLeastOneLocation
    multiAssetsEnabled
    allowedMultiAssetSchemaIds
    requireAtLeastOneMultiAsset
    requiredMultiAssetSchemaIds
  }
  ${BASE_TASK_FIELD}
`;

export const TASK_CREATE: TypedDocumentNode<
  { taskCreate: Task },
  { input: TaskCreateInput }
> = gql`
  mutation taskCreate($input: TaskCreateInput!) {
    taskCreate(input: $input) {
      ...BaseTask
    }
  }
  ${BASE_TASK}
`;

export const TASK_UPDATE: TypedDocumentNode<
  { taskUpdate: Task },
  { input: TaskUpdateInput }
> = gql`
  mutation taskUpdate($input: TaskUpdateInput!) {
    taskUpdate(input: $input) {
      ...BaseTask
    }
  }
  ${BASE_TASK}
`;

export const TASK_DELETE: TypedDocumentNode<
  { taskDelete: boolean },
  { workOrderId: string; taskId: string }
> = gql`
  mutation taskDelete($workOrderId: ID!, $taskId: ID!) {
    taskDelete(workOrderId: $workOrderId, taskId: $taskId)
  }
`;

export const TASK_DUPLICATE: TypedDocumentNode<
  { taskDuplicate: { id: string; name: string } },
  { workOrderId: string; taskId: string }
> = gql`
  mutation taskDuplicate($workOrderId: ID!, $taskId: ID!) {
    taskDuplicate(workOrderId: $workOrderId, taskId: $taskId) {
      id
      name
    }
  }
`;

export const TASK_USER_GROUP_ADD: TypedDocumentNode<
  { taskUserGroupsAdd: Task },
  { input: TaskUserGroupsAddInput }
> = gql`
  mutation taskUserGroupsAdd($input: TaskUserGroupsAddInput!) {
    taskUserGroupsAdd(input: $input) {
      ...BaseTask
    }
  }
  ${BASE_TASK}
`;

export const TASK_USER_GROUP_REMOVE: TypedDocumentNode<
  { taskUserGroupRemove: Task },
  { input: TaskUserGroupRemoveInput }
> = gql`
  mutation taskUserGroupRemove($input: TaskUserGroupRemoveInput!) {
    taskUserGroupRemove(input: $input) {
      ...BaseTask
    }
  }
  ${BASE_TASK}
`;

export const TASK_USER_ADD: TypedDocumentNode<
  { taskUsersAdd: Task },
  { input: TaskUsersAddInput }
> = gql`
  mutation taskUsersAdd($input: TaskUsersAddInput!) {
    taskUsersAdd(input: $input) {
      ...BaseTask
    }
  }
  ${BASE_TASK}
`;

export const TASK_USER_REMOVE: TypedDocumentNode<
  { taskUserRemove: Task },
  { input: TaskUserRemoveInput }
> = gql`
  mutation taskUserRemove($input: TaskUserRemoveInput!) {
    taskUserRemove(input: $input) {
      ...BaseTask
    }
  }
  ${BASE_TASK}
`;

export const TASK_FIELD_CREATE: TypedDocumentNode<
  { taskFieldCreate: TaskField },
  { input: TaskFieldCreateInput }
> = gql`
  mutation taskFieldCreate($input: TaskFieldCreateInput!) {
    taskFieldCreate(input: $input) {
      ...BaseTaskField
    }
  }
  ${BASE_TASK_FIELD}
`;

export const TASK_FIELD_UPDATE: TypedDocumentNode<
  { taskFieldUpdate: TaskField },
  { input: TaskFieldUpdateInput }
> = gql`
  mutation taskFieldUpdate($input: TaskFieldUpdateInput!) {
    taskFieldUpdate(input: $input) {
      ...BaseTaskField
    }
  }
  ${BASE_TASK_FIELD}
`;

export const TASK_FIELD_DELETE: TypedDocumentNode<
  { taskFieldDelete: boolean },
  { input: TaskFieldDeleteInput }
> = gql`
  mutation taskFieldDelete($input: TaskFieldDeleteInput!) {
    taskFieldDelete(input: $input)
  }
`;

export const TASK_MATERIALS_ADD: TypedDocumentNode<
  { taskMaterialsAdd: TaskMaterialItem[] },
  { input: TaskMaterialsAddInput }
> = gql`
  mutation taskMaterialsAdd($input: TaskMaterialsAddInput!) {
    taskMaterialsAdd(input: $input) {
      name
      assetId
      colorId
      rate
      quantity
      unit
    }
  }
`;

export const TASK_USERS_STATUS_UPDATE: TypedDocumentNode<
  { taskUsersStatusUpdate: boolean },
  { input: TaskUsersStatusUpdateInput }
> = gql`
  mutation taskUsersStatusUpdate($input: TaskUsersStatusUpdateInput!) {
    taskUsersStatusUpdate(input: $input)
  }
`;

export const TASK_ASSETS_ADD: TypedDocumentNode<
  { taskAssetsAdd: string[] },
  { input: TaskAssetsAddInput }
> = gql`
  mutation taskAssetsAdd($input: TaskAssetsAddInput!) {
    taskAssetsAdd(input: $input)
  }
`;

export const TASK_ASSET_REMOVE: TypedDocumentNode<
  { taskAssetRemove: boolean },
  { input: TaskAssetRemoveInput }
> = gql`
  mutation taskAssetRemove($input: TaskAssetRemoveInput!) {
    taskAssetRemove(input: $input)
  }
`;

export const RELATED_FIELD_AUTOCOMPLETE: TypedDocumentNode<
  { relatedFieldDataAutocomplete: RelatedFieldAutocomplete },
  { input: RelatedFieldInput }
> = gql`
  mutation relatedFieldDataAutocomplete($input: RelatedFieldInput!) {
    relatedFieldDataAutocomplete(input: $input) {
      components {
        title
        dataType
        enumeration
      }
    }
  }
`;
